import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import FilterListIcon from '@material-ui/icons/FilterList';
import blue from '@material-ui/core/colors/blue';
import grey from '@material-ui/core/colors/grey';

function createData(name, calories, fat, carbs, protein) {
    return { name, calories, fat, carbs, protein };
}


function descendingComparator(a, b, orderBy) {
    let item1 = a[orderBy];
    let item2 = b[orderBy];
    // this is ratio kematian
    if (orderBy === 'protein') {
        item1 = parseFloat(a[orderBy].split('%')[0]);
        item2 = parseFloat(b[orderBy].split('%')[0]);
    }
    if (item2 < item1) {
        return -1;
    }
    if (item2 > item1) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'name', numeric: false, disablePadding: true, label: 'Provinsi' },
    { id: 'calories', numeric: true, disablePadding: false, label: 'Positif COVID-19' },
    { id: 'fat', numeric: true, disablePadding: false, label: 'Sembuh' },
    { id: 'carbs', numeric: true, disablePadding: false, label: 'Meninggal Dunia' },
    { id: 'protein', numeric: true, disablePadding: false, label: 'Rasio Kematian' },
];

function EnhancedTableHead(props) {
    const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        style={{paddingLeft: 16, paddingRight: 16}}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            <strong>{headCell.label}</strong>
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
    title: {
        flex: '1 1 100%',
    },
}));

const EnhancedTableToolbar = (props) => {
    const classes = useToolbarStyles();
    const { numSelected } = props;

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            {numSelected > 0 ? (
                <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                    Nutrition
                </Typography>
            )}

            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton aria-label="delete">
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Filter list">
                    <IconButton aria-label="filter list">
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
    root: {
        width: '80%',
        margin: 'auto',
        [theme.breakpoints.down('md')]: {
            width: '90%',
        }
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    table: {
        minWidth: 750,
        [theme.breakpoints.down('md')]: {
            minWidth: 520,
        },
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
}));

export default function EnhancedTable() {
    const classes = useStyles();
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('calories');
    const [selected, setSelected] = React.useState([]);
    const [dense, setDense] = React.useState(false);
    const [rows, setRows] = React.useState([]);
    // const [total, setTotal] = React.useState({});

    useEffect(() => {
        const fetchData = async () => {
            const provinceData = await (await fetch('https://api.kawalcorona.com/indonesia/provinsi/')).json();
            // const totalData = await (await fetch('https://api.kawalcorona.com/indonesia/')).json();
            const cases = provinceData.map(({attributes: {Provinsi, Kasus_Posi, Kasus_Semb, Kasus_Meni}}) => createData(Provinsi, Kasus_Posi || 0, Kasus_Semb || 0, Kasus_Meni || 0, `${Math.round((Kasus_Meni/Kasus_Posi*100) * 10) / 10}%`));
            // let totalCases = {
            //     confirmed: totalData[0].positif.replace(',', ''),
            //     recovered: totalData[0].sembuh,
            //     deaths: totalData[0].meninggal
            // };
            setRows(cases);
            // setTotal(totalCases);
        };
        fetchData();
    }, []);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = rows.map((n) => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    return (rows.length > 0 &&
        <>
        <div className={classes.root}>
            <div style={{textAlign: 'left', marginBottom: 8}}>Kasus Positif COVID-19</div>
            <Table className={classes.table} aria-label="simple table" style={{maxWidth: 554, minWidth: 240}}>
                <TableHead>
                    <TableRow>
                        <TableCell style={{backgroundColor: grey[500], width: 109, height: 12, padding: 0}}></TableCell>
                        <TableCell style={{backgroundColor: blue[100], width: 109, height: 12, padding: 0}}></TableCell>
                        <TableCell style={{backgroundColor: blue[500], width: 109, height: 12, padding: 0}}></TableCell>
                        <TableCell style={{backgroundColor: blue[700], width: 109, height: 12, padding: 0}}></TableCell>
                        <TableCell style={{backgroundColor: blue[900], width: 109, height: 12, padding: 0}}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell style={{padding: 0, border: 0}}>0</TableCell>
                        <TableCell style={{padding: 0, border: 0}}>1-49</TableCell>
                        <TableCell style={{padding: 0, border: 0}}>50-499</TableCell>
                        <TableCell style={{padding: 0, border: 0}}>500-999</TableCell>
                        <TableCell style={{padding: 0, border: 0}}>1000+</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Paper className={classes.paper}>
                <TableContainer>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={'small'}
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {stableSort(rows, getComparator(order, orderBy))
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.name);
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) => handleClick(event, row.name)}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.name}
                                            selected={isItemSelected}
                                        >
                                            <TableCell component="th" id={labelId} scope="row" padding="none" style={{paddingLeft: 16, paddingRight: 16}} className={classes.row}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell align="right" className={classes.row}>{row.calories}</TableCell>
                                            <TableCell align="right" className={classes.row}>{row.fat}</TableCell>
                                            <TableCell align="right" className={classes.row}>{row.carbs}</TableCell>
                                            <TableCell align="right" className={classes.row}>{row.protein}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </div>
        </>
    );
}
